import { removeTrapFocus, trapFocus } from "./focus.js";

const EVENTS = {
  OPEN: "predictive-search:open",
  CLOSE: "predictive-search:close",
};

class PredictiveSearch extends HTMLElement {
  /**
   * Set an abort controller to cancel in-flight search requests.
   * @type {AbortController|null}
   */
  #activeFetchController = null;
  #initialEmptyState = null;
  #triggerElement = null;

  constructor() {
    super();
    this.boundOnInput = this.onInput.bind(this);
    this.boundOnSearchKeyDown = this.onSearchKeyDown.bind(this);
    this.boundOnClick = this.onClick.bind(this);
    this.boundOnOpen = this.onOpen.bind(this);
    this.boundOnClickOutside = this.onClickOutside.bind(this);
  }

  connectedCallback() {
    this.elements = {
      input: this.querySelector("[data-quick-search='input']"),
      results: this.querySelector("[data-quick-search='results']"),
      form: this.querySelector("form"),
    };

    this.#initialEmptyState = this.elements.results.innerHTML;

    /**
     * Only add the input event listener if quick search is enabled.
     * - Allows the empty state to still be shown but not updated on input if quick search is disabled.
     * - User can still submit the form to see the search results for their query.
     */
    const enableQuickSearch =
      this.getAttribute("data-quick-search-enabled") === "true";
    if (enableQuickSearch) {
      this.elements.input.addEventListener("input", this.boundOnInput);
    }
    this.elements.input.addEventListener("keydown", this.boundOnSearchKeyDown);
    this.elements.form.addEventListener("keydown", this.boundOnSearchKeyDown);
    document.body.addEventListener("click", this.boundOnClickOutside);
    this.addEventListener("click", this.boundOnClick);
    window.addEventListener(EVENTS.OPEN, this.boundOnOpen);
  }

  get isOpen() {
    return this.getAttribute("data-state") === "open";
  }

  onInput(event) {
    this.search(event);
  }

  onSearchKeyDown(event) {
    if (event.key !== "Escape" || !this.isOpen) {
      return;
    }

    /**
     * if the input is focused and has a value, then we return early so that the escape key can be used to clear the
     * input value without closing the predictive search.
     */
    if (
      document.activeElement === this.elements.input &&
      this.elements.input.value
    ) {
      return;
    }

    this.onClose();
  }

  onClick(event) {
    if (event.target.closest("[data-quick-search='suggestion']")) {
      this.#handleSuggestionClick(event);
    }

    if (event.target.closest("[data-quick-search='close-button']")) {
      this.onClose();
    }
  }

  /**
   * Handle clicks outside the predictive search form to close it.
   * - If the click is outside the predictive search form or the trigger that opens it, close the predictive search.
   * - We check the predictive-search form rather than the whole predictive-search component to allow for clicks on the
   *   overlay to close the search.
   */
  onClickOutside(event) {
    if (
      this.isOpen &&
      !event.target.closest("predictive-search form") &&
      !event.target.closest("predictive-search-trigger")
    ) {
      this.onClose();
    }
  }

  onOpen(event) {
    // Store the trigger element for focus return (only if it's focusable)
    if (event.target && typeof event.target.focus === "function") {
      /**
       * If the trigger element is a <predictive-search-trigger>, we want to store the button inside it as the trigger
       * element for returning focus, since the <predictive-search-trigger> itself is not focusable, but the button
       * inside it is.
       * - Can access the button directly from the `.elements.button` property of the component.
       */
      if (event.target instanceof PredictiveSearchTrigger) {
        this.#triggerElement = event.target.elements.button;
      } else {
        this.#triggerElement = event.target;
      }
    }

    // Closes any open navigation panels on desktop.
    if (document.documentElement.hasAttribute("data-navigation-hovered")) {
      document.documentElement.removeAttribute("data-navigation-hovered");
    }

    // Closes any open navigation panels on mobile.
    document.body.dispatchEvent(
      new CustomEvent("Navigation:Close", {
        bubbles: true,
      }),
    );

    // Closes any open country selector panels.
    document.body.dispatchEvent(
      new CustomEvent("CountrySelector:Close", {
        bubbles: true,
      }),
    );

    this.removeAttribute("inert");

    if (this.elements.input) {
      this.elements.input.value = "";
      this.elements.input.focus();
    }

    this.setAttribute("data-state", "open");

    document.documentElement.classList.add("overflow-hidden");

    trapFocus(this);
  }

  onClose() {
    this.setAttribute("data-state", "closed");
    window.dispatchEvent(new CustomEvent(EVENTS.CLOSE, { bubbles: true }));
    document.documentElement.classList.remove("overflow-hidden");
    this.#resetSearch();
    this.setAttribute("inert", "");

    // Return focus to the element that opened the search
    removeTrapFocus(this.#triggerElement);
    this.#triggerElement = null;
  }

  #handleSuggestionClick(event) {
    const searchTerm = event.target
      .closest("[data-quick-search='suggestion']")
      .getAttribute("data-value");

    this.elements.input.value = searchTerm;
    this.#getSearchResults(searchTerm);
  }

  #createAbortController() {
    const abortController = new AbortController();

    // Abort any in-flight request before creating a new one
    if (this.#activeFetchController) {
      this.#activeFetchController.abort();
    }

    this.#activeFetchController = abortController;
    return abortController;
  }

  search = this.debounce((event) => {
    const searchTerm = this.elements.input.value.trim();

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    if (this.#checkForCustomRedirect(searchTerm)) {
      return;
    }

    this.#getSearchResults(searchTerm);
  }, 200);

  #checkForCustomRedirect(searchTerm) {
    /**
     * @type {Array<{searchTerm: Array<string>, url: string}>}
     */
    const redirectData = window.searchCustomRedirects || [];
    if (!redirectData.length) return;

    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    const redirect = redirectData.find((entry) => {
      return entry.searchTerm.some(
        (term) => term.toLowerCase() === lowerCaseSearchTerm,
      );
    });

    if (redirect) {
      window.location.href = redirect.url;
      return true;
    }
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(
      window.Shopify.routes.predictiveSearchUrl,
      window.location.origin,
    );
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[limit]", 5);
    url.searchParams.set("section_id", this.dataset.sectionId);

    try {
      const abortController = this.#createAbortController();
      const response = await fetch(url, { signal: abortController.signal });
      const html = await response.text();

      this.elements.results.innerHTML = html;
    } catch (error) {
      if (error.name === "AbortError") {
        // Expected error due to aborting previous request, no need to log it
        return;
      }
      console.error("Error fetching search results:", error);
    }
  }

  async #resetSearch() {
    this.elements.input.value = "";

    await this.#showEmptyState();
  }

  /**
   * Fetch the empty state content for the predictive search results and update the DOM with it.
   */
  async #showEmptyState() {
    const content = await this.#getEmptyStateContent();
    this.elements.results.innerHTML = content;
  }

  /**
   * Get the empty state content for the predictive search results.
   * - If the initial empty state content was available on component initialization, return that to avoid an unnecessary fetch.
   * - Otherwise, fetch the empty state content from the server using the section ID specified in the component's data attributes.
   * @returns {Promise<string>} The HTML content for the empty state.
   */
  async #getEmptyStateContent() {
    if (this.#initialEmptyState) {
      return this.#initialEmptyState;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("page");
    url.searchParams.set("section_id", "quick-search-empty");

    try {
      const abortController = this.#createAbortController();

      const response = await fetch(url, { signal: abortController.signal });
      const html = await response.text();

      this.#initialEmptyState = html; // Cache the empty state content for future use
      return html;
    } catch (error) {
      if (error.name === "AbortError") {
        // Expected error due to aborting previous request, no need to log it
        return;
      }
      console.error("Error resetting search results:", error);
    }
  }

  /**
   * Creates a debounced function that delays calling the provided function (fn)
   * until after wait milliseconds have elapsed since the last time
   * the debounced function was invoked. The returned function has a .cancel()
   * method to cancel any pending calls.
   * @param {Function} fn the function to debounce
   * @param {Number} wait the number of milliseconds to delay
   * @returns {Function} a debounced version of the provided function, with a .cancel() method
   */
  debounce(fn, wait) {
    let timeout;

    function debounced(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), wait);
    }

    // Add the .cancel method:
    debounced.cancel = () => {
      clearTimeout(timeout);
    };

    return debounced;
  }
}

if (!customElements.get("predictive-search")) {
  customElements.define("predictive-search", PredictiveSearch);
}

class PredictiveSearchTrigger extends HTMLElement {
  constructor() {
    super();
    this.boundOnClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.elements = {
      button: this.querySelector("button"),
    };
    this.elements.button.addEventListener("click", this.boundOnClick);
  }

  onClick() {
    const event = new CustomEvent(EVENTS.OPEN, {
      bubbles: true,
    });
    this.dispatchEvent(event);
  }
}

if (!customElements.get("predictive-search-trigger")) {
  customElements.define("predictive-search-trigger", PredictiveSearchTrigger);
}
